if (!customElements.get("upsell-product-manager")) {
  class UpsellProductManager extends HTMLElement {
    constructor() {
      super();
      this.upsellCheckboxes = [];
      this.init();
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.upsellCheckboxes = document.querySelectorAll(
        ".upsell-checkbox-input"
      );
      console.log(
        "UpsellProductManager initialized, found checkboxes:",
        this.upsellCheckboxes.length
      );
      this.bindEvents();
    }

    /**
     * CRITICAL: Event Binding - Form Submission Interception
     * Listens for ALL form submissions on the page
     * Checks if the form is an add-to-cart form (data-type="add-to-cart-form")
     * If upsells are checked, this will prevent default submission
     * and handle adding both main product + upsells via Cart API
     */
    bindEvents() {
      // Listen for form submissions on product forms
      document.addEventListener("submit", (e) => {
        if (
          e.target.matches('[data-type="add-to-cart-form"]') ||
          e.target.closest('[data-type="add-to-cart-form"]') ||
          e.target.matches('form[action*="/cart/add"]')
        ) {
          this.handleFormSubmit(e);
        }
      });
    }

    /**
     * CRITICAL: Form Submit Handler
     * Checks if any upsell checkboxes are checked
     * If YES: Prevents default form submission and uses custom cart logic
     * If NO: Allows normal Shopify form submission (standard add-to-cart)
     */
    handleFormSubmit(e) {
      const checkedUpsells = this.getCheckedUpsells();
      console.log("Upsell form submitted, checked upsells:", checkedUpsells);
      if (checkedUpsells.length > 0) {
        e.preventDefault();
        this.addProductsToCart(checkedUpsells, e.target);
      }
    }

    getCheckedUpsells() {
      const checkedUpsells = [];
      this.upsellCheckboxes.forEach((checkbox) => {
        if (checkbox.checked) {
          checkedUpsells.push({
            id: checkbox.dataset.upsellVariantId,
            productId: checkbox.dataset.upsellProductId,
            sellingPlan: checkbox.dataset.upsellSellingPlan,
          });
        }
      });
      return checkedUpsells;
    }

    async addProductsToCart(upsellProducts, originalForm) {
      try {
        const submitButton = originalForm.querySelector(
          'button[type="submit"]'
        );
        this.setLoadingState(submitButton, true);

        // Get the main product form data
        const mainFormData = new FormData(originalForm);
        const mainProductId = mainFormData.get("id");
        const mainProductQuantity = mainFormData.get("quantity") || 1;
        const mainSellingPlan = mainFormData.get("selling_plan");

        if (!mainProductId) {
          throw new Error("Main product ID not found");
        }

        // Prepare items to add to cart
        const items = [
          {
            id: mainProductId,
            quantity: parseInt(mainProductQuantity),
          },
        ];

        // Add selling plan to main product if it exists
        if (mainSellingPlan) {
          items[0].selling_plan = mainSellingPlan;
        }

        /**
         * CRITICAL: Build Cart Items Array
         * Creates array with:
         * 1. MAIN PRODUCT: from the product form (with optional selling plan for subscriptions)
         * 2. UPSELL PRODUCTS: from checked upsell checkboxes (each with their own selling plan)
         * 
         * Format for each item:
         * {
         *   id: "variant_id",
         *   quantity: 1,
         *   selling_plan: "plan_id" (optional, for subscriptions)
         * }
         */
        // Add upsell products with their selling plans
        upsellProducts.forEach((upsell) => {
          const upsellItem = {
            id: upsell.id,
            quantity: 1,
          };

          if (upsell.sellingPlan) {
            upsellItem.selling_plan = upsell.sellingPlan;
          }

          items.push(upsellItem);
        });

        /**
         * CRITICAL: Add to Cart via Shopify Cart API
         * POST to /cart/add.js with items array
         * Adds MULTIPLE products in a single request (main + upsells)
         * On success:
         *   - Opens cart drawer
         *   - Updates cart icon count
         *   - Unchecks upsell checkboxes
         *   - Dispatches cart:updated event
         */
        // Add to cart
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items: items }),
        });

        if (response.ok) {
          this.triggerCartUpdate();
          this.uncheckUpsells();
        } else {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Failed to add products to cart"
          );
        }
      } catch (error) {
        console.error("Error adding products to cart:", error);
      } finally {
        const submitButton = originalForm.querySelector(
          'button[type="submit"]'
        );
        this.setLoadingState(submitButton, false);
      }
    }

    setLoadingState(button, isLoading) {
      if (!button) return;

      const spinner = button.querySelector(".loading-overlay__spinner");
      const buttonText = button.querySelector(
        "span:not(.loading-overlay__spinner)"
      );

      if (isLoading) {
        button.disabled = true;
        if (spinner) spinner.classList.remove("hidden");
        if (buttonText) buttonText.style.opacity = "0.5";
      } else {
        button.disabled = false;
        if (spinner) spinner.classList.add("hidden");
        if (buttonText) buttonText.style.opacity = "1";
      }
    }

    /**
     * CRITICAL: Trigger Cart UI Updates
     * After successful cart addition:
     * 1. Opens the cart drawer (slides in from right)
     * 2. Updates cart icon bubble with new item count
     * 3. Dispatches cart:updated event for other components
     */
    triggerCartUpdate() {
      // Trigger cart drawer update if it exists
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        cartDrawer.open();
      }

      // Trigger cart icon update
      const cartIcon = document.querySelector("cart-icon-bubble");
      if (cartIcon) {
        cartIcon.updateCart();
      }

      // Dispatch custom event for other components
      document.dispatchEvent(new CustomEvent("cart:updated"));
    }

    uncheckUpsells() {
      this.upsellCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
    }
  }

  customElements.define("upsell-product-manager", UpsellProductManager);
}

document.addEventListener("DOMContentLoaded", () => {
  document.body.appendChild(document.createElement("upsell-product-manager"));
});

document.addEventListener("shopify:section:load", () => {
  const existingManager = document.querySelector("upsell-product-manager");
  if (existingManager) {
    existingManager.init();
  }
});
